import { IH2OTheme } from '@h2oai/ui-kit';
import { ChartData as CData, ChartOptions } from 'chart.js';

import { resolveColor } from '../../../utils/utils';

export const lineChartOptions = (title: string, dataLength?: number, titleFontSize?: number): ChartOptions<'line'> => ({
  responsive: true,
  plugins: {
    legend: { position: 'bottom' },
    title: {
      display: !!title,
      text: title,
      font: {
        size: titleFontSize,
        weight: 'bold',
      },
    },
  },
  elements: {
    point: {
      radius: dataLength ? (dataLength <= 1 ? 4 : 2) : 2,
      hoverRadius: 4,
      hitRadius: 10,
    },
  },
  scales: {
    x: {
      ticks: {
        maxTicksLimit: 12,
      },
    },
  },
});

interface ChartDataset {
  label: string;
  data: number[];
  // Add other ChartJS dataset properties as needed
}

interface ChartData {
  labels: string[];
  datasets: ChartDataset[];
}

export interface NestedRawChartData {
  [date: string]: {
    [workloadType: string]: number;
  };
}

export interface FlatRawChartData {
  [date: string]: number | bigint;
}

export const transformNestedDataForCharts = (
  data: NestedRawChartData,
  labelMapping: Record<string, string> = {},
  colorMapping: Record<string, string> = {},
  enableAreaFill = false
): ChartData => {
  const allWorkloadTypes = new Set<string>();
  Object.values(data).forEach((dayData: { [workloadType: string]: number }) => {
    Object.keys(dayData).forEach((workload: string) => {
      allWorkloadTypes.add(workload);
    });
  });

  const workloadTypesArray: string[] = Array.from(allWorkloadTypes).sort();

  const dates: string[] = Object.keys(data).sort();
  const formattedDates: string[] = dates.map((date: string) =>
    new Date(date).toLocaleDateString('en-US', {
      year: '2-digit',
      month: 'short',
      day: 'numeric',
    })
  );

  const datasets = workloadTypesArray.map((workloadType: string, _: number) => {
    const dataPoints: number[] = dates.map((date: string) => {
      return data[date][workloadType] || 0;
    });

    const label =
      labelMapping[workloadType] ?? workloadType.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

    const color = colorMapping[workloadType] || '#CCCCCC';

    const dataset: any = {
      label,
      data: dataPoints,
      backgroundColor: enableAreaFill && workloadType !== 'threshold' ? `${color}80` : `${color}B3`,
      borderColor: color,
      borderWidth: 2,
      tension: 0.4,
    };

    if (enableAreaFill && workloadType !== 'threshold') {
      // Apply area fill to all datasets except threshold
      dataset.fill = true;
    } else if (workloadType === 'threshold') {
      // Keep threshold as a line chart with different styling
      dataset.fill = false;
      dataset.borderWidth = 3;
      dataset.borderDash = [5, 5];
      dataset.pointRadius = 0;
      dataset.pointHoverRadius = 6;
      dataset.backgroundColor = 'transparent';
    }

    return dataset;
  });

  return { labels: formattedDates, datasets };
};

export const transformFlatDataForCharts = (data: FlatRawChartData, label: string, color = '#e16385'): ChartData => {
  const keys: string[] = Object.keys(data).sort();

  const isDate = keys.every((key) => !isNaN(Date.parse(key)));

  const labels: string[] = isDate
    ? keys.map((date: string) =>
        new Date(date).toLocaleDateString('en-US', {
          year: '2-digit',
          month: 'short',
          day: 'numeric',
        })
      )
    : keys;

  const values: number[] = keys.map((key: string) => data[key] as number);

  const dataset = {
    label,
    data: values,
    backgroundColor: `${color}BC`,
    borderColor: color,
    borderWidth: 2,
    fill: false,
    tension: 0.4,
  };

  return {
    labels,
    datasets: [dataset],
  };
};

export const transformNestedDataForCategoricalCharts = (
  data: NestedRawChartData,
  labelMapping: Record<string, string> = {},
  colorMapping: Record<string, string> = {},
  xAxisLabelMapping: Record<string, string> = {}
): ChartData => {
  const allWorkloadTypes = new Set<string>();
  Object.values(data).forEach((dayData) => {
    Object.keys(dayData).forEach((workload) => {
      allWorkloadTypes.add(workload);
    });
  });

  const workloadTypesArray = Array.from(allWorkloadTypes).sort();
  const xAxisKeys = Object.keys(data).sort();

  const labels = xAxisKeys.map((key) => xAxisLabelMapping[key] || key);

  const datasets = workloadTypesArray.map((workloadType) => {
    const dataPoints = xAxisKeys.map((key) => data[key][workloadType] || 0);

    const label =
      labelMapping[workloadType] ?? workloadType.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

    const color = colorMapping[workloadType] || '#CCCCCC';

    return {
      label,
      data: dataPoints,
      backgroundColor: `${color}B3`,
      borderColor: color,
      borderWidth: 2,
      tension: 0.4,
    };
  });

  return { labels, datasets };
};

export const transformFlatDataForPieChart = (
  data: FlatRawChartData,
  colorMap?: Record<string, string>,
  labelMap?: Record<string, string>,
  defaultColor = '#e16385'
): CData => {
  const keys: string[] = Object.keys(data).sort();

  const labels: string[] = keys.map((key: string) => labelMap?.[key] || key);

  const values: number[] = keys.map((key: string) => data[key] as number);

  const backgroundColor: string[] = keys.map((key: string) => colorMap?.[key] || defaultColor);

  const dataset = {
    data: values,
    backgroundColor,
    borderWidth: 1,
  };

  return {
    labels,
    datasets: [dataset],
  };
};

export function getDummyList<T>(length: number, defaultValue?: T): T[] {
  if (defaultValue === undefined) {
    const inferredDefault = {} as Partial<T>;

    return Array.from({ length }, () => inferredDefault as T);
  }

  return Array.from({ length }, () => ({ ...defaultValue }));
}

export function getAiUnitComponentColor(theme: IH2OTheme, component: string, defaultColor: string): string {
  const colorMap: Record<string, string> = {
    'Document AI': theme.palette?.blue300 || defaultColor,
    h2oGPT: theme.palette?.green600 || defaultColor,
    'h2oGPT + LLM': theme.palette?.green600 || defaultColor,
    'h2ogpte-playground': theme.palette?.red300 || defaultColor,
    MLOps: theme.palette?.red500 || defaultColor,
    'Document.ai': theme.palette?.red600 || defaultColor,
    'Doc.ai': theme.palette?.red600 || defaultColor,
    'AIEM - DAI\\H2O-3': theme.palette?.yellow500 || defaultColor,
    'App-Store': theme.palette?.green300 || defaultColor,
    Steam: theme.palette?.yellow600 || defaultColor,
    'ML-API': theme.palette?.yellow400 || defaultColor,
    eScorer: theme.palette?.blue700 || defaultColor,
    'MLOps Deployments': theme.palette?.blue500 || defaultColor,
    'Notebook Workload': theme.palette?.green900 || defaultColor,
    'AppStore Applications': theme.palette?.red900 || defaultColor,
  };

  return resolveColor(colorMap[component] || theme.palette?.gray300, defaultColor);
}

export function getGaugeMetricComponentColor(theme: IH2OTheme, component: string, defaultColor: string): string {
  const colorMap: Record<string, string> = {
    Users: theme.palette?.blue300 || defaultColor,
    Projects: theme.palette?.green300 || defaultColor,
    Datasets: theme.palette?.red300 || defaultColor,
    MLOps: theme.palette?.yellow500 || defaultColor,
    Experiments: theme.palette?.red600 || defaultColor,
    'Registered Models': theme.palette?.green500 || defaultColor,
    'Registered Model Versions': theme.palette?.blue500 || defaultColor,
    Deployments: theme.palette?.yellow700 || defaultColor,
  };

  return resolveColor(colorMap[component] || theme.palette?.gray300, defaultColor);
}

export const getDefaultDateRange = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(today.getMonth() - 1);
  oneMonthAgo.setHours(0, 0, 0, 0);

  return {
    startDate: oneMonthAgo,
    endDate: today,
  };
};

export type UserContext = {
  userEmail: string;
  sourceIpAddress: string;
  userAgent: string;
};

export async function buildUserContext(user: { name?: string | null }): Promise<UserContext> {
  let ip = '0.0.0.0';
  try {
    const res = await fetch('https://api.ipify.org?format=json');
    const data = await res.json();
    ip = data.ip;
  } catch (err) {
    console.warn('Failed to fetch public IP:', err);
  }

  return {
    userEmail: user?.name ?? 'unknown',
    sourceIpAddress: ip,
    userAgent: navigator.userAgent,
  };
}

export const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};
